import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    alpha,
    Tooltip,
    Box
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import useAuth from '../hooks/useAuth';
import AddBalanceModal from '../pages/dashboard/operator/AddBalanceModal';
import AddManualTradeModal from '../pages/dashboard/operator/AddManualTradeModal';
import { PATH_DASHBOARD } from '../routes/paths';

const BrokerDataTable = ({
    data = [],
    formatCurrency,
    onSettingsClick,
    onUsernameClick,
    maxHeight = 'calc(100vh - 120px)', // Increased height to match right pane
    showSettings = true,
    selectedRows = [],
    isShare = false,
    selectedBroker = null,
    selectedAdmin = null,
    isBrokerView = false,
    isAdminView = false,
    onUserUpdate, // Add this prop for handling user updates
    showActionButtons = true, // Control for showing action buttons
    isAdmin = false // Flag for admin-specific actions
}) => {// State for modals
    const [openBalanceModal, setOpenBalanceModal] = useState(false);
    const [openTradeModal, setOpenTradeModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const { user } = useAuth(); // Get the authenticated user
    const navigate = useNavigate();    // Settings click handler with navigation logic similar to UserConfigInfo
    const handleSettingsClick = (row) => {
        if (onSettingsClick) {
            // If onSettingsClick prop is provided, use it
            onSettingsClick(row);
        } else {
            // Otherwise, use the navigation logic similar to UserConfigInfo
            let navigateToURL = '';
            if (user.role.rolename === 'admin') {
                navigateToURL = isBrokerView ? `/admin/client/edit/` : `/admin/broker/edit/`;
                console.log('condition Admin view:', navigateToURL);
            } else if (user.role.rolename === 'superAdmin') {
                // SuperAdmin navigation based on current view state
                if (selectedBroker) {
                    // Viewing broker's clients - navigate to client edit
                    navigateToURL = `/superadmin/client/edit/`;
                } else if (selectedAdmin || isAdminView) {
                    // Viewing admin's brokers - navigate to broker edit
                    navigateToURL = `/superadmin/broker/edit/`;
                } else {
                    // Viewing admins - navigate to admin edit
                    navigateToURL = `/superadmin/admin/edit/`;
                }
            } else if (user.role.rolename === 'broker') {
                // For brokers, always navigate to client edit since they only see clients
                navigateToURL = `/broker/client/edit/`;
            } else {
                navigateToURL = `/broker/client/edit/`;
            }
            // Use trader._id if available, otherwise fall back to username
            const userId = row.trader?._id || row._id || row.username;
            navigate(`${navigateToURL}${userId}`);
        }
    };    // Modal handlers
    const handleOpenBalanceModal = (user) => {
        // Use the original API data which has the correct field names for AddBalanceModal
        const balanceSummary = user.originalData || user;
        setSelectedUser(balanceSummary);
        setOpenBalanceModal(true);
    };

    const handleCloseBalanceModal = () => {
        setOpenBalanceModal(false);
        setSelectedUser(null);
    };

    const handleOpenTradeModal = (user) => {
        setSelectedUser(user);
        setOpenTradeModal(true);
    };

    const handleCloseTradeModal = () => {
        setOpenTradeModal(false);
        setSelectedUser(null);
    };    // Calculate totals
    const calculateTotals = () => {
        const rowsToCalculate = isShare ? selectedRows : data;
        return rowsToCalculate.reduce((acc, row) => {
            // Helper function to parse formatted values
            const parseFormattedValue = (value) => {
                if (!value || value === '₹0' || value === '0') return 0;
                // Remove currency symbol, commas, and any other non-numeric characters except minus sign
                const cleaned = String(value).replace(/[₹,\s]/g, '').trim();
                return parseFloat(cleaned) || 0;
            };

            // Check if properties exist before accessing them
            if (row.grossPL) acc.grossPL += parseFormattedValue(row.grossPL);
            if (row.activePL) acc.activePL += parseFormattedValue(row.activePL);
            if (row.bookedPL) acc.bookedPL += parseFormattedValue(row.bookedPL);
            if (row.brk) acc.brk += parseFormattedValue(row.brk);
            if (row.refunBrk) acc.refunBrk += parseFormattedValue(row.refunBrk);
            return acc;
        }, {
            grossPL: 0,
            activePL: 0,
            bookedPL: 0,
            brk: 0,
            refunBrk: 0
        });
    };

    const totals = calculateTotals();
    const displayData = isShare ? selectedRows : data;    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: maxHeight === 'none' ? 'fit-content' : '100%',
            width: '100%'
        }}>            <TableContainer component={Paper} sx={(theme) => ({
                mt: 0.5,
                flexGrow: maxHeight === 'none' ? 0 : 1,
                overflowY: maxHeight === 'none' ? 'visible' : 'auto',
                overflowX: 'auto', // Always allow horizontal scrolling
                border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: theme.spacing(1),
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                    width: 6,
                    height: 6
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: alpha(theme.palette.background.neutral, 0.3),
                    borderRadius: 2,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.4),
                    borderRadius: 2,
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[500], 0.6),
                    },
                },
                '& .MuiTable-root': {
                    borderCollapse: 'collapse',
                },
                '& .MuiTableCell-root': {
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    py: 1,
                    px: 2,
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    '&:last-child': {
                        borderRight: 'none',
                    }
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    borderBottom: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                    backgroundColor: alpha(theme.palette.primary.lighter, 0.08),
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                },
                '& .frozen-column': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: '#fff',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        height: '100%',
                        width: '4px',
                        background: `linear-gradient(90deg, rgba(0,0,0,0.08), transparent)`,
                    }
                },
                '& .MuiTableHead-root .frozen-column': {
                    backgroundColor: alpha(theme.palette.primary.lighter, 0.12),
                    zIndex: 15,
                    boxShadow: `4px 0 8px -4px ${alpha(theme.palette.common.black, 0.1)}`,
                },
                '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .frozen-column': {
                    backgroundColor: '#f5f5f5',
                    backdropFilter: 'blur(8px)',
                },
                '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .frozen-column': {
                    backgroundColor: '#ffffff',
                    backdropFilter: 'blur(8px)',
                },
                '& .MuiTableBody-root .MuiTableRow-root:hover .frozen-column': {
                    backgroundColor: '#edf4ff',
                },
                '& .totals-row': {
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 5,
                    backgroundColor: '#e0e0e0',
                    '& .MuiTableCell-root': {
                        backgroundColor: '#e0e0e0',
                        borderTop: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
                        fontWeight: 'bold',
                    },
                    '& .frozen-column': {
                        backgroundColor: '#e0e0e0 !important',
                        zIndex: 6,
                        boxShadow: `4px 0 8px -4px ${alpha(theme.palette.common.black, 0.2)}`,
                    }
                },
            })}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {showSettings && (
                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Settings
                                </TableCell>
                            )}
                            <TableCell
                                className="frozen-column"
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                Username
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                Credit
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                GrossPL
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                ActivePL
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                BookedPL
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                Brk
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    backgroundColor: 'background.paper',
                                    fontWeight: 'bold',
                                }}
                            >
                                Refun Brk
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayData.map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    '&:hover': {
                                        backgroundColor: 'background.neutral',
                                    },
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: 'action.hover',
                                    },
                                    height: '36px', // Reduced row height
                                }}
                            >
                                {showSettings && (
                                    <TableCell align="center" sx={{ padding: '4px 12px' }}>
                                        {(isBrokerView || user.role.rolename === 'broker') && showActionButtons ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Tooltip title="Add Balance">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenBalanceModal(row)}
                                                        sx={{
                                                            color: 'primary.main',
                                                            padding: '2px',
                                                            '&:hover': {
                                                                color: 'primary.dark',
                                                            },
                                                        }}
                                                    >
                                                        <MonetizationOnIcon sx={{ fontSize: '0.9rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {user.role.rolename === "admin" && (
                                                    <Tooltip title="Add Trade">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenTradeModal(row)}
                                                            sx={{
                                                                color: 'primary.main',
                                                                padding: '2px',
                                                                '&:hover': {
                                                                    color: 'primary.dark',
                                                                },
                                                            }}
                                                        >
                                                            <AddIcon sx={{ fontSize: '0.9rem' }} />
                                                        </IconButton>
                                                    </Tooltip>)}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSettingsClick(row)}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        padding: '2px',
                                                        '&:hover': {
                                                            color: 'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <SettingsIcon sx={{ fontSize: '0.9rem' }} />
                                                </IconButton>
                                            </Box>) : (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleSettingsClick(row)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    padding: '2px',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            >
                                                <SettingsIcon sx={{ fontSize: '0.9rem' }} />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                )}
                                <TableCell
                                    className="frozen-column"
                                    component="th" scope="row"
                                    sx={{
                                        fontWeight: 'medium',
                                        padding: '4px 12px',  // Reduced padding
                                    }}
                                >
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            display: 'inline-block',
                                            backgroundColor: selectedBroker === row.username ? alpha('#1976d2', 0.95) : alpha('#1976d2', 0.85), color: 'white',
                                            padding: '4px 10px',  // Reduced padding
                                            borderRadius: '4px',  // Smaller border radius
                                            cursor: 'pointer',
                                            fontWeight: 500, fontSize: '0.75rem',  // Smaller font size
                                            transition: 'all 0.2s ease',
                                            boxShadow: selectedBroker === row.username ? '0 2px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.12)',
                                            lineHeight: 1.2,  // Tighter line height
                                        }}
                                        onClick={() => {
                                            if (isBrokerView && user.role.rolename === 'superAdmin' && selectedBroker) {
                                                // SuperAdmin in broker view clicking on client - navigate to client trades
                                                const clientId = row.trader?._id || row._id;
                                                if (clientId && selectedAdmin) {
                                                    navigate(`/superadmin/dashboard/${selectedAdmin}/broker/${selectedBroker}/client/${clientId}`);
                                                }
                                            }
                                            else if (isBrokerView && user.role.rolename === 'admin') {
                                                // Admin is in broker view, clicking on client should navigate to broker/client/id
                                                const clientId = row.trader?._id || row._id;
                                                if (clientId) {
                                                    navigate(PATH_DASHBOARD.insurerRole.broker.client.view(clientId));
                                                }
                                            }
                                            else if ((isBrokerView && user.role.rolename === 'broker') || user.role.rolename === 'broker') {
                                                // Broker clicking on client should navigate to their client view
                                                const clientId = row.trader?._id || row._id;
                                                if (clientId) {
                                                    navigate(PATH_DASHBOARD.operatorRole.clients.view(clientId));
                                                }
                                            }
                                            else if (onUsernameClick && !isBrokerView) {
                                                onUsernameClick(row.username);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (onUsernameClick && (e.key === 'Enter' || e.key === ' ')) {
                                                e.preventDefault();
                                                onUsernameClick(row.username);
                                            }
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isBrokerView) {
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isBrokerView) {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }
                                        }}
                                        onFocus={(e) => {
                                            if (!isBrokerView) {
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (!isBrokerView) {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }
                                        }}>
                                        {row.username || '-'}
                                    </span>
                                    {row.trader?.isDemoAccount === true && (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-block',
                                                backgroundColor: '#f0f0f0', // light gray background
                                                color: 'primary.light',
                                                padding: '3px 5px',
                                                marginLeft: '2px',
                                                borderRadius: '10px',
                                                fontSize: '0.60rem',
                                                fontWeight: 'bold',
                                                border: '2px solid #ccc',
                                            }}
                                        >
                                            DEMO
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: 'black',
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.balance === "-0" ? "0" : row.balance || '₹0'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: row.grossPL && row.grossPL.startsWith('-') && row.grossPL !== "-0" ? '#c62828' : '#1b5e20',
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.grossPL === "-0" ? "0" : row.grossPL || '₹0'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: row.activePL && row.activePL.startsWith('-') && row.activePL !== "-0" ? '#c62828' : '#1b5e20', // Dark red and dark green
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.activePL === "-0" ? "0" : row.activePL || '₹0'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: row.bookedPL && row.bookedPL.startsWith('-') ? '#c62828' : '#1b5e20', // Dark red and dark green
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.bookedPL || '₹0'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: 'blue',
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.brk || '₹0'}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        color: 'blue',
                                        fontWeight: 'medium',
                                        fontSize: '0.75rem',
                                        padding: '4px 12px',
                                    }}
                                >
                                    {row.refunBrk || '₹0'}
                                </TableCell>
                            </TableRow>
                        ))}

                        {/* Totals row */}
                        <TableRow className="totals-row">
                            {showSettings &&
                                <TableCell align="center" />}
                            <TableCell align="center" />
                            <TableCell
                                className="frozen-column"
                                sx={{
                                    fontWeight: 'bold',
                                }}
                            >
                                TOTAL
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: +totals.grossPL >= 0 ? '#1b5e20' : '#c62828', // Dark green and dark red
                                    fontWeight: 'bold',
                                }}
                            >
                                {formatCurrency(totals.grossPL)}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: +totals.activePL >= 0 ? '#1b5e20' : '#c62828', // Dark green and dark red
                                    fontWeight: 'bold',
                                }}
                            >
                                {formatCurrency(totals.activePL)}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: totals.bookedPL >= 0 ? '#1b5e20' : '#c62828', // Dark green and dark red
                                    fontWeight: 'bold',
                                }}
                            >
                                {formatCurrency(totals.bookedPL)}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: 'blue', // Dark green and dark red
                                    fontWeight: 'bold',
                                }}
                            >
                                {formatCurrency(totals.brk)}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: 'blue', // Dark green and dark red
                                    fontWeight: 'bold',
                                }}
                            >
                                {formatCurrency(totals.refunBrk)}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modals */}
            {(isBrokerView || user.role.rolename === 'broker') && selectedUser && (
                <>
                    {openBalanceModal && (
                        <AddBalanceModal
                            open={openBalanceModal}
                            balanceSummary={selectedUser}
                            onClose={handleCloseBalanceModal}
                            onUserUpdate={onUserUpdate}
                        />
                    )}

                    {openTradeModal && (
                        <AddManualTradeModal
                            open={openTradeModal}
                            client={{ username: selectedUser.username }}
                            onClose={handleCloseTradeModal}
                            onUserUpdate={onUserUpdate}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

BrokerDataTable.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            username: PropTypes.string,
            grossPL: PropTypes.string,
            activePL: PropTypes.string,
            bookedPL: PropTypes.string,
            brk: PropTypes.string,
            refunBrk: PropTypes.string,
            trader: PropTypes.object,
        })
    ), formatCurrency: PropTypes.func.isRequired,
    onSettingsClick: PropTypes.func,
    onUsernameClick: PropTypes.func, maxHeight: PropTypes.string,
    showSettings: PropTypes.bool,
    selectedRows: PropTypes.array,
    isShare: PropTypes.bool,
    selectedBroker: PropTypes.string,
    isBrokerView: PropTypes.bool,
    onUserUpdate: PropTypes.func,
    showActionButtons: PropTypes.bool,
    isAdmin: PropTypes.bool
};

export default BrokerDataTable;
